/**
 * useVersionCheck — wires the version-skew triggers.
 *
 * Theme 4. Detects when a newer build has been deployed while this tab stayed
 * open, and surfaces a prompt-first signal so the user can refresh on their own
 * terms (never auto-reload — see the dirty-state guard).
 *
 * Triggers (all funnel through a single throttled check):
 *   1. window focus / document visibilitychange (tab brought back to front)
 *   2. a slow interval (default 20 min) for tabs that stay focused
 *   3. dynamic-chunk load error — Vite's `vite:preloadError`. After a deploy the
 *      old hashed chunk 404s; today that's an unhandled crash. We convert it into
 *      the refresh prompt instead.
 *
 * The hook itself does NOT render anything. `VersionUpdateBanner` consumes it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  APP_VERSION,
  fetchLatestVersion,
  isVersionMismatch,
  type VersionManifest,
} from '@/lib/version/versionCheck';
import { isAppDirty } from '@/lib/version/dirtyState';

const DEFAULT_INTERVAL_MS = 20 * 60 * 1000; // 20 min — slow on purpose
const MIN_CHECK_GAP_MS = 30 * 1000; // throttle: at most one network check / 30s

export interface UseVersionCheckOptions {
  /** Override the slow poll interval (ms). */
  intervalMs?: number;
  /** Inject fetch for tests. */
  fetchImpl?: typeof fetch;
  /** Inject the running app's version for tests. */
  currentVersion?: string;
}

export interface UseVersionCheckResult {
  /** True once a real version mismatch has been observed. Sticky — stays true. */
  updateAvailable: boolean;
  /** The latest manifest seen from the server (null until a successful fetch). */
  latest: VersionManifest | null;
  /**
   * Hard-reload the page to pick up the new bundle. Prompt-first: if the app has
   * dirty/in-progress state, asks for native confirmation before reloading so an
   * accidental click can't discard an in-progress invoice/proposal edit.
   * Returns true if the reload was initiated.
   */
  refreshNow: () => boolean;
  /** Force a check immediately (used by the chunk-error path and tests). */
  checkNow: () => void;
}

export function useVersionCheck(
  options: UseVersionCheckOptions = {}
): UseVersionCheckResult {
  const {
    intervalMs = DEFAULT_INTERVAL_MS,
    fetchImpl = fetch,
    currentVersion = APP_VERSION,
  } = options;

  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latest, setLatest] = useState<VersionManifest | null>(null);

  // Refs that the stable event handlers read, so we don't re-subscribe on every
  // state change.
  const lastCheckRef = useRef(0);
  const updateAvailableRef = useRef(false);
  const inFlightRef = useRef(false);

  const runCheck = useCallback(
    async (force = false) => {
      // Once we know an update is available, stop polling — the answer won't
      // change and the banner is already up.
      if (updateAvailableRef.current) return;
      if (inFlightRef.current) return;

      const now = Date.now();
      if (!force && now - lastCheckRef.current < MIN_CHECK_GAP_MS) return;
      lastCheckRef.current = now;

      inFlightRef.current = true;
      try {
        const manifest = await fetchLatestVersion(fetchImpl);
        if (!manifest) return; // null = no signal, never prompt
        setLatest(manifest);
        if (isVersionMismatch(currentVersion, manifest.version)) {
          updateAvailableRef.current = true;
          setUpdateAvailable(true);
        }
      } finally {
        inFlightRef.current = false;
      }
    },
    [currentVersion, fetchImpl]
  );

  const checkNow = useCallback(() => {
    void runCheck(true);
  }, [runCheck]);

  // Trigger 1: focus + visibilitychange.
  useEffect(() => {
    const onFocus = () => void runCheck();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void runCheck();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [runCheck]);

  // Trigger 2: slow interval.
  useEffect(() => {
    const id = window.setInterval(() => void runCheck(), intervalMs);
    return () => window.clearInterval(id);
  }, [runCheck, intervalMs]);

  // Trigger 3: dynamic-chunk load error after a deploy.
  // Vite fires `vite:preloadError` on the window when a lazily-imported chunk
  // fails to load (typically a hashed file that no longer exists post-deploy).
  // We preventDefault so it doesn't bubble to the global error handler as an
  // unhandled crash, then force an immediate version check which raises the
  // refresh prompt.
  useEffect(() => {
    const onPreloadError = (event: Event) => {
      event.preventDefault();
      void runCheck(true);
      // If we already know (or quickly confirm) a skew, the banner handles it.
      // As a hard fallback, if version.json is somehow unreachable but a chunk
      // genuinely 404'd, still surface the prompt — a missing chunk is itself
      // strong evidence of a deploy.
      if (!updateAvailableRef.current) {
        updateAvailableRef.current = true;
        setUpdateAvailable(true);
      }
    };
    window.addEventListener('vite:preloadError', onPreloadError);
    return () => window.removeEventListener('vite:preloadError', onPreloadError);
  }, [runCheck]);

  // Initial check on mount (covers a tab opened just before a deploy that then
  // sits focused; the interval would otherwise be the first opportunity).
  useEffect(() => {
    void runCheck(true);
  }, [runCheck]);

  const refreshNow = useCallback((): boolean => {
    // Prompt-first safety: never silently reload over dirty state.
    if (isAppDirty()) {
      const proceed = window.confirm(
        'You have unsaved changes. Reloading to the new version will discard them. Reload anyway?'
      );
      if (!proceed) return false;
    }
    // Hashed assets + no-cache HTML make a plain hard reload sufficient; the new
    // service worker (autoUpdate) will also have taken over by now.
    window.location.reload();
    return true;
  }, []);

  return { updateAvailable, latest, refreshNow, checkNow };
}
